/**
 * Contextual Magnetic Physics Cursor & Floating Image Follower
 * Features: Universal Event Delegation, Contextual Dynamic Badges, Smooth Easing
 */

export class CustomCursor {
  constructor() {
    this.cursor = document.getElementById('nbk-cursor');
    this.label = document.getElementById('nbk-cursor-label');
    this.floatingPreview = document.getElementById('nbk-floating-preview');
    this.floatingImg = document.getElementById('nbk-floating-preview-img');

    if (!this.cursor) return;

    this.mouse = { x: -100, y: -100 };
    this.pos = { x: -100, y: -100 };
    this.previewPos = { x: -100, y: -100 };
    this.speed = 0.22;
    this.previewSpeed = 0.12;
    this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.isReducedMotion || window.innerWidth < 768) {
      return;
    }

    this.init();
  }

  init() {
    window.addEventListener(
      'mousemove',
      (e) => {
        this.mouse.x = e.clientX;
        this.mouse.y = e.clientY;
        this.cursor.style.opacity = '1';
      },
      { passive: true },
    );

    // Universal Event Delegation for Magnetic Cursor & Contextual Text Badges
    document.addEventListener('mouseover', (e) => {
      // 1. Text input or textarea: precision mode (hide ring, show minimal dot)
      if (e.target.closest('input, textarea, select')) {
        this.cursor.classList.add('is-input');
        this.cursor.classList.remove('is-hovering', 'has-label');
        if (this.label) this.label.textContent = '';
        return;
      }
      this.cursor.classList.remove('is-input');

      // 2. Interactive Buttons, Navigation Pills, and Links:
      // Transparent, unobtrusive focus ring. ZERO text badge, ZERO opaque blocking fill!
      const interactiveTarget = e.target.closest(
        'a, button, [role="button"], .nbk-audio-btn, .nbk-available-btn, .nbk-branding',
      );

      if (interactiveTarget) {
        this.cursor.classList.add('is-hovering');
        this.cursor.classList.remove('has-label');
        if (this.label) this.label.textContent = '';
        return;
      }

      // 3. Lightbox modal check:
      // If hovering outside .nbk-lightbox-content (the dark backdrop), display CLOSE action badge.
      // If hovering inside the dialog or on the image, STRICTLY suppress any CLOSE label.
      const lightboxModal = e.target.closest('#nbk-lightbox-modal');
      if (lightboxModal) {
        if (!e.target.closest('.nbk-lightbox-content')) {
          if (this.label) this.label.textContent = 'CLOSE';
          this.cursor.classList.add('has-label', 'is-hovering');
        } else {
          this.cursor.classList.remove('has-label', 'is-hovering');
          if (this.label) this.label.textContent = '';
        }
        return;
      }

      // 4. Large visual media viewports that warrant a contextual action badge (DRAG, VIEW, SLIDE)
      const mediaTarget = e.target.closest(
        '[data-cursor], .spiral-card, .spiral-card-inner, .curved-preview-card, .curved-cylinder-viewport, .curved-screen-item, .curved-slider-dot, [data-preview-img]',
      );

      if (mediaTarget) {
        let badgeText = mediaTarget.getAttribute('data-cursor');
        if (!badgeText) {
          if (
            mediaTarget.closest('.curved-preview-card') ||
            mediaTarget.closest('.curved-screen-item') ||
            mediaTarget.closest('.spiral-card') ||
            mediaTarget.closest('.spiral-card-inner')
          ) {
            badgeText = 'VIEW';
          } else if (mediaTarget.closest('.curved-cylinder-viewport')) {
            badgeText = 'DRAG';
          } else if (mediaTarget.closest('.curved-slider-dot')) {
            badgeText = 'SLIDE';
          }
        }

        if (badgeText && this.label) {
          this.label.textContent = badgeText;
          this.cursor.classList.add('has-label', 'is-hovering');
        } else {
          this.cursor.classList.add('is-hovering');
          this.cursor.classList.remove('has-label');
          if (this.label) this.label.textContent = '';
        }

        // Floating Image Preview on Project List Items
        const previewSrc = mediaTarget.getAttribute('data-preview-img');
        if (previewSrc && this.floatingPreview && this.floatingImg) {
          this.floatingImg.src = previewSrc;
          this.floatingPreview.classList.add('is-active');
        }
        return;
      }

      // 5. Default outside interactive elements
      this.cursor.classList.remove('is-hovering', 'has-label');
      if (this.label) this.label.textContent = '';
    });

    document.addEventListener('mouseout', (e) => {
      const target = e.target.closest(
        'a, button, [role="button"], input, textarea, select, #nbk-lightbox-modal, .spiral-card, .spiral-card-inner, .curved-preview-card, .curved-cylinder-viewport, .curved-screen-item, .curved-slider-dot, [data-preview-img], [data-cursor]',
      );

      if (target && !target.contains(e.relatedTarget)) {
        this.cursor.classList.remove('is-hovering', 'has-label', 'is-input');
        if (this.label) this.label.textContent = '';

        if (this.floatingPreview) {
          this.floatingPreview.classList.remove('is-active');
        }
      }
    });

    // Tactile Click / Active State
    window.addEventListener('mousedown', () => {
      this.cursor.classList.add('is-active');
    });
    window.addEventListener('mouseup', () => {
      this.cursor.classList.remove('is-active');
    });

    this.render();
  }

  render() {
    // Cursor spring easing
    this.pos.x += (this.mouse.x - this.pos.x) * this.speed;
    this.pos.y += (this.mouse.y - this.pos.y) * this.speed;
    this.cursor.style.transform = `translate3d(${this.pos.x}px, ${this.pos.y}px, 0)`;

    // Floating image preview smoother spring easing
    if (this.floatingPreview) {
      this.previewPos.x += (this.mouse.x - this.previewPos.x) * this.previewSpeed;
      this.previewPos.y += (this.mouse.y - this.previewPos.y) * this.previewSpeed;
      this.floatingPreview.style.transform = `translate3d(${this.previewPos.x}px, ${this.previewPos.y}px, 0)`;
    }

    requestAnimationFrame(() => this.render());
  }
}
